import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AircraftRegisterEntryDetailsDto,
    AuthorityRegisterSummaryDto,
    AuthorityTypeDto,
    CraftService,
    DateFormatService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionRegisterStatusDetails,
    OrganisationAuthorityDto,
    OrganisationAuthorityService,
    RegisterEntryVerificationActivityDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { AuthorityDialoguesService } from 'libs/authorities/src/lib/authority-dialogues.service';
import { matchConditionChecks } from 'libs/authorities/src/lib/registers/helpers';
import * as moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RpaAddToRegisterDialogue } from '../add-to-register-dialogue/rpa-add-to-register-dialogue.component';

interface Columns {
    heading: string;
    check: boolean;
}

interface Row {
    status: AircraftRegisterEntryDetailsDto.Status;
    columns: Columns[];
}

interface Register {
    heading: string;
    subtitle: string;
    isMissionRegister: boolean;
    id: number;
    rows: Row[];
    canView: boolean;
}
@Component({
    selector: 'rpa-registers-status',
    templateUrl: './rpa-registers-status.component.html',
    styles: [
        `
            .status-column {
                width: 10%;
                min-width: 60px;
            }
        `
    ],
    styleUrls: ['./styles.scss']
})
export class RpaRegistersStatusComponent implements OnInit, OnDestroy {
    @Input() managingOrganisationId: number;
    @Input() rpaId: number;
    @Input() canEdit: boolean;
    @Input() canAdminOrganisation: boolean;
    @Input() missionDetails: MissionRegisterStatusDetails;

    time: string;
    missionName: string;
    registers: Register[];
    allRegisters: AircraftRegisterEntryDetailsDto[];
    allAuthorities: OrganisationAuthorityDto[];

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();
    working: boolean;

    constructor(
        private craftService: CraftService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private logging: FlyFreelyLoggingService,
        private modalService: BsModalService,
        private dateFormatService: DateFormatService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        if (this.missionDetails == null) {
            this.time = moment().toISOString();
        } else {
            this.time = this.missionDetails.missionDate;
            this.missionName = this.missionDetails.missionName;
        }
        const today = moment().format('YYYY-MM-DD');
        forkJoin({
            shared: this.organisationAuthorityService.findSharedAuthorities(
                this.managingOrganisationId,
                today
            ),
            authorities: this.organisationAuthorityService.findAuthorities(
                this.managingOrganisationId,
                this.managingOrganisationId
            )
        })
            .subscribe({
                next: ({ shared, authorities }) => {
                    const sharedAuthorities = shared.reduce(
                        (acc, group) => acc.concat(group.authorities ?? []),
                        []
                    );
                    const normalAuthorities = authorities.reduce(
                        (acc, group) => acc.concat(group.authorities ?? []),
                        []
                    );
                    this.allAuthorities = [
                        ...normalAuthorities,
                        ...sharedAuthorities
                    ];
                    this.refreshData();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error fetching authorities for RPA: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshData() {
        this.registers = [];
        this.craftService
            .findAircraftRegisterEntryByAircraftId(
                this.rpaId,
                this.managingOrganisationId,
                this.time.toString()
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(result => this.mapRegisters(result))
            .add(this.workTracker.createTracker());
    }

    mapRegisters(registers: AircraftRegisterEntryDetailsDto[]) {
        if (registers == null) {
            this.allRegisters = [];
            return;
        }
        const format = this.dateFormatService.getDateTimeFormat();
        this.allRegisters = registers;
        registers.forEach(r => {
            const relevantAuthority = this.allAuthorities.find(
                a => a.id === r.authority.id
            );
            const authorityRegister = relevantAuthority?.registers.find(
                reg => reg.id === r.authorityRegisterId
            );
            const authorityRegisterName = authorityRegister?.name;
            const isMissionRegister =
                this.missionDetails != null
                    ? r.authority.id ===
                      this.missionDetails.delegatedAuthorityId
                    : false;
            const isDisabled =
                authorityRegister.registerMode ===
                AuthorityRegisterSummaryDto.RegisterMode.DISABLED;
            const isArchivedAuthority = relevantAuthority.archived;
            const title = isMissionRegister
                ? `${r.authority.organisation.name} ${
                      r.authority.identifier ?? ''
                  } ${authorityRegisterName} (Mission Register)`
                : `${r.authority.organisation.name} ${
                      r.authority.identifier ?? ''
                  } ${authorityRegisterName}`;
            const titleWithStatus = isArchivedAuthority
                ? `${title} (Authority Archived)`
                : isDisabled
                ? `${title} (Disabled)`
                : title;
            const subtitle =
                this.missionName != null
                    ? isMissionRegister
                        ? `Relevant mission register status as at ${moment(
                              this.time
                          ).format(format)} as required by ${this.missionName}`
                        : `Showing register status as at ${moment(
                              this.time
                          ).format(format)} as required by ${this.missionName}`
                    : '';
            const cols: Columns[] = r.conditions.map(c => ({
                heading: c.name,
                check:
                    r.checks.find(check => matchConditionChecks(c, check))
                        ?.status ===
                    RegisterEntryVerificationActivityDto.Status.PASSED
            }));
            const index = this.registers.findIndex(
                reg => reg.heading === titleWithStatus
            );
            if (index === -1) {
                this.registers.push({
                    heading: titleWithStatus,
                    subtitle: subtitle,
                    id: r.id,
                    isMissionRegister: isMissionRegister,
                    rows: [
                        {
                            status: r.status,
                            columns: cols
                        }
                    ],
                    canView:
                        r.authority.organisation.id ===
                        this.managingOrganisationId
                });
            } else {
                this.registers[index].rows.push({
                    status: r.status,
                    columns: cols
                });
            }
        });
        // Sort relevant mission registers to the top
        this.registers.sort((a, b) =>
            a.isMissionRegister
                ? -1
                : b.isMissionRegister
                ? 1
                : a.heading.localeCompare(b.heading)
        );
    }

    showRegister(register: Register) {
        const reg = this.allRegisters.find(r => register.id === r.id);
        this.organisationAuthorityService
            .findAuthority(reg.authority.id, this.managingOrganisationId)
            .subscribe({
                next: authority => {
                    const dialogue =
                        this.authorityDialoguesService.showRpaRegisterDialogue(
                            reg.authority.organisation.id,
                            <AuthorityTypeDto>reg.authority.authorityType,
                            {
                                ...authority
                            },
                            authority.registers.find(
                                r => r.id === reg.authorityRegisterId
                            ),
                            this.managingOrganisationId
                        );
                    dialogue.onHidden
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(() => this.refreshData());
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error viewing register: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    showAddToRegisterDialogue() {
        const dialogue = this.modalService.show(RpaAddToRegisterDialogue, {
            class: 'modal-md',
            initialState: {
                rpaId: this.rpaId,
                managingOrganisationId: this.managingOrganisationId
            }
        });
        dialogue.onHidden
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshData());
    }
}
